import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import styled from 'styled-components';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LoginRequest } from '../types';
import toast from 'react-hot-toast';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 420px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  .logo-icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 1.5rem;
    margin: 0 auto 16px;
  }
  
  h1 {
    margin: 0 0 8px;
    font-size: 1.875rem;
    font-weight: 700;
    color: #1f2937;
  }
  
  p {
    margin: 0;
    color: #6b7280;
    font-size: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(237, 32, 36, 0.1)'};
  }
  
  &.password {
    padding-right: 48px;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s;
  
  &:hover {
    color: #374151;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 4px;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(237, 32, 36, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Login: React.FC = () => {
  const { user, login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LoginRequest>();

  // Handle auto-login from URL parameters
  useEffect(() => {
    // Only attempt auto-login once and if not already loading
    if (autoLoginAttempted || isLoading || user) {
      return;
    }

    const email = searchParams.get('email');
    const password = searchParams.get('password');
    const autoLogin = searchParams.get('autoLogin') === 'true';

    // If email and password are provided, populate the form
    if (email) {
      setValue('email', email);
    }
    
    if (password) {
      setValue('password', password);
    }

    // If autoLogin flag is set and credentials are provided, auto-submit
    if (autoLogin && email && password) {
      setAutoLoginAttempted(true);
      // Set iframe mode flag for UI adjustments
      localStorage.setItem('iframeMode', 'true');
      const submitLogin = async () => {
        try {
          await login({ email, password });
          // In iframe mode, redirect to calendar instead of dashboard
          navigate('/calendar');
        } catch (error) {
          console.error('Auto-login failed:', error);
          // Error is already handled by auth context with toast
        }
      };
      submitLogin();
    }
  }, [searchParams, setValue, login, navigate, autoLoginAttempted, isLoading, user]);

  // Redirect if already logged in
  if (user && !isLoading) {
    // Check if we're in iframe mode to redirect to calendar instead of dashboard
    const iframeMode = localStorage.getItem('iframeMode') === 'true';
    return <Navigate to={iframeMode ? '/calendar' : '/'} replace />;
  }

  const onSubmit: SubmitHandler<LoginRequest> = async (data) => {
    try {
      await login(data);
      navigate('/');
    } catch (error) {
      // Error is handled by the auth context
      console.error('Login failed:', error);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <div className="logo-icon">TF</div>
          <h1>TimeForge</h1>
          <p>Shop Scheduler Management</p>
        </Logo>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              hasError={!!errors.email}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Please enter a valid email address'
                }
              })}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <InputContainer>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="password"
                hasError={!!errors.password}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </PasswordToggle>
            </InputContainer>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>
          
          <LoginButton type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  border: '2px solid rgba(255,255,255,0.3)', 
                  borderTop: '2px solid white', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }} />
                Signing In...
              </>
            ) : (
              <>
                <LogIn />
                Sign In
              </>
            )}
          </LoginButton>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;